import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { useFormContext } from "react-hook-form";
import { Link } from "react-router-dom";

import {
  ICaseRateCptConflict,
  useCaseRateCptConflicts,
} from "../hooks/useCaseRateCptConflicts";

interface ICaseRateConflictBannerProps {
  excludeRulesetId?: string;
}

/** Conflicts regrouped for display: one row per owning ruleset, with its clashing codes. */
interface IConflictRow {
  rulesetId: string;
  rulesetName: string;
  cptCodes: string[];
}

function groupByRuleset(conflicts: ICaseRateCptConflict[]): IConflictRow[] {
  const byRuleset = new Map<string, IConflictRow>();
  conflicts.forEach((conflict) => {
    const row = byRuleset.get(conflict.rulesetId) ?? {
      rulesetId: conflict.rulesetId,
      rulesetName: conflict.rulesetName,
      cptCodes: [],
    };
    if (!row.cptCodes.includes(conflict.cptCode)) {
      row.cptCodes.push(conflict.cptCode);
    }
    byRuleset.set(conflict.rulesetId, row);
  });
  return [...byRuleset.values()];
}

/**
 * CLE-2568 — the warning names the exact clashing codes and links the ruleset that owns each of
 * them, and "Remove conflicting code(s)" strips them from the form in one click instead of the
 * user hunting them down in the CPT chips by hand.
 *
 * The button edits only THIS form — it never touches the other ruleset. If a code should move
 * here rather than stay there, the owning ruleset's edit page is one click away via the link.
 */
export function CaseRateConflictBanner({
  excludeRulesetId,
}: ICaseRateConflictBannerProps) {
  const { getValues, setValue } = useFormContext();
  const { conflictsByCombo, comboNames, conflictingComboCount, totalCombos } =
    useCaseRateCptConflicts({ excludeRulesetId });

  if (conflictsByCombo.size === 0) return null;

  const comboWord = totalCombos === 1 ? "combination" : "combinations";
  const headline =
    totalCombos === 1
      ? "One or more of the CPT codes you've added already belong to another case rate ruleset for this provider and payer. Remove or change the conflicting code(s) before saving."
      : `${conflictingComboCount} of ${totalCombos} provider/payer ${comboWord} include CPT code(s) that already belong to another case rate ruleset for the same provider and payer. Remove or change the conflicting code(s) before saving.`;

  const allConflictingCodes = new Set(
    [...conflictsByCombo.values()].flat().map((conflict) => conflict.cptCode)
  );

  const handleRemoveConflictingCodes = () => {
    const currentCodes: string[] = getValues("cptCodes") || [];
    setValue(
      "cptCodes",
      currentCodes.filter((code) => !allConflictingCodes.has(code)),
      { shouldValidate: true, shouldDirty: true }
    );
  };

  const content = (
    <div className="w-full py-2">
      <p className="m-0 font-semibold">{headline}</p>
      <ul className="mt-2 mb-0 pl-3">
        {[...conflictsByCombo.entries()].map(([comboKey, conflicts]) => {
          const names = comboNames.get(comboKey);
          const comboLabel =
            totalCombos > 1 && names
              ? `${names.providerName} / ${names.payerName}: `
              : "";
          return groupByRuleset(conflicts).map((row) => (
            <li key={`${comboKey}:${row.rulesetId}`} className="mb-1">
              {comboLabel}
              <strong>{row.cptCodes.join(", ")}</strong> — already in{" "}
              <Link
                to={`/payer-rules/case-rate/edit/${row.rulesetId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {row.rulesetName}
              </Link>
            </li>
          ));
        })}
      </ul>
      <Button
        type="button"
        label={`Remove conflicting code${
          allConflictingCodes.size === 1 ? "" : "s"
        }`}
        icon="pi pi-times"
        className="p-button-sm p-button-outlined p-button-warning mt-2"
        onClick={handleRemoveConflictingCodes}
      />
    </div>
  );

  return (
    <div className="mb-4">
      <Message severity="warn" content={content} className="w-full" />
    </div>
  );
}
